/**
 * AdminReliabilityTab — Phase A.
 *
 * Subtabs: Errors, API Failures, Auth, Payments, Performance.
 * (Auth / Payments / Performance ship Phase A as basic digests; deeper
 * dashboards land in Phase B.)
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, ServerCrash, Apple, CreditCard, Gauge } from "lucide-react";

const POLL_MS = 8000;
const POLL_BLURRED_MS = 60000;

function timeAgo(iso) {
  if (!iso) return "—";
  const sec = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}

const CATEGORY_BADGE = {
  react_crash: { label: "Crash", colour: "bg-red-500/15 text-red-400" },
  uncaught: { label: "Uncaught", colour: "bg-amber-500/15 text-amber-400" },
  apple_signin: { label: "Apple Sign-In", colour: "bg-zinc-500/15 text-zinc-300" },
  native_bridge: { label: "Native bridge", colour: "bg-purple-500/15 text-purple-300" },
  yir_render: { label: "YIR", colour: "bg-pink-500/15 text-pink-400" },
  upload: { label: "Upload", colour: "bg-blue-500/15 text-blue-400" },
  api_failure: { label: "API", colour: "bg-orange-500/15 text-orange-400" },
};

function useAdaptivePolling(fn) {
  useEffect(() => {
    fn();
    let iv;
    const start = () => {
      clearInterval(iv);
      iv = setInterval(fn, document.hidden ? POLL_BLURRED_MS : POLL_MS);
    };
    const onVis = () => start();
    start();
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(iv); document.removeEventListener("visibilitychange", onVis); };
  }, [fn]);
}

const AdminReliabilityTab = () => {
  const [errors, setErrors] = useState([]);
  const [apiFailures, setApiFailures] = useState([]);
  const [paymentHealth, setPaymentHealth] = useState({ failures: [], total_payment_failures_7d: 0 });

  const fetchAll = useCallback(async () => {
    try {
      const [eR, fR, pR] = await Promise.all([
        axios.get(`${API}/admin/errors`),
        axios.get(`${API}/admin/api-failures`),
        axios.get(`${API}/admin/payment-health`),
      ]);
      setErrors(eR.data?.errors || []);
      setApiFailures(fR.data?.failures || []);
      setPaymentHealth(pR.data || { failures: [], total_payment_failures_7d: 0 });
    } catch (_) {}
  }, []);

  useAdaptivePolling(fetchAll);

  const appleSigninCount = errors.filter((e) => e.category === "apple_signin").reduce((s, e) => s + (e.count || 0), 0);
  const apiTotal = apiFailures.reduce((s, f) => s + (f.count || 0), 0);
  const crashes = errors.filter((e) => e.category === "react_crash").reduce((s, e) => s + (e.count || 0), 0);

  return (
    <div className="space-y-4" data-testid="admin-reliability">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard icon={<ServerCrash className="w-4 h-4 text-red-400" />} label="React crashes (7d)" value={crashes} />
        <KpiCard icon={<AlertTriangle className="w-4 h-4 text-orange-400" />} label="API failures" value={apiTotal} />
        <KpiCard icon={<Apple className="w-4 h-4 text-zinc-300" />} label="Apple Sign-In errors" value={appleSigninCount} />
        <KpiCard icon={<CreditCard className="w-4 h-4 text-pink-400" />} label="Payment fails (7d)" value={paymentHealth.total_payment_failures_7d} />
      </div>

      <Tabs defaultValue="errors" className="space-y-4">
        <div className="overflow-x-auto -mx-4 px-4">
          <TabsList className="bg-card border border-border inline-flex min-w-max">
            <TabsTrigger value="errors"><ServerCrash className="w-3.5 h-3.5 mr-1" />Errors</TabsTrigger>
            <TabsTrigger value="api"><AlertTriangle className="w-3.5 h-3.5 mr-1" />API Failures</TabsTrigger>
            <TabsTrigger value="auth"><Apple className="w-3.5 h-3.5 mr-1" />Auth</TabsTrigger>
            <TabsTrigger value="payments"><CreditCard className="w-3.5 h-3.5 mr-1" />Payments</TabsTrigger>
            <TabsTrigger value="performance"><Gauge className="w-3.5 h-3.5 mr-1" />Performance</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="errors">
          <Card>
            <CardHeader><CardTitle className="text-base">Frontend errors</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {errors.length === 0 && <p className="text-sm text-muted-foreground">No frontend errors captured. 🎉</p>}
              {errors.map((er, idx) => {
                const meta = CATEGORY_BADGE[er.category] || { label: er.category_label || er.category, colour: "bg-zinc-500/15 text-zinc-300" };
                return (
                  <div key={`${er.category}-${er.message}-${idx}`} className="p-3 bg-muted/20 rounded-lg" data-testid={`reliability-error-${idx}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={`${meta.colour} border-0`}>{meta.label}</Badge>
                      <span className="text-sm font-medium truncate flex-1">{er.message}</span>
                      <Badge variant="outline" className="text-[10px]">x{er.count}</Badge>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-1">
                      {er.route || "(no route)"} · {er.users_affected} user{er.users_affected === 1 ? "" : "s"} · latest {timeAgo(er.latest)}
                      {er.latest_user_name && <span> · {er.latest_user_name}</span>}
                    </p>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="api">
          <Card>
            <CardHeader><CardTitle className="text-base">API failures (4xx / 5xx)</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {apiFailures.length === 0 && <p className="text-sm text-muted-foreground">No API failures recorded. 🎉</p>}
              {apiFailures.map((f, idx) => (
                <div key={`${f.endpoint}-${f.status}-${idx}`} className="p-3 bg-muted/20 rounded-lg" data-testid={`reliability-api-${idx}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className="bg-orange-500/15 text-orange-400 border-0">{f.status || "?"}</Badge>
                    <code className="text-xs flex-1 truncate">{f.endpoint}</code>
                    <Badge variant="outline" className="text-[10px]">x{f.count}</Badge>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    {f.users_affected} user{f.users_affected === 1 ? "" : "s"} · latest {timeAgo(f.latest)}
                    {f.latest_response_ms ? <span> · ~{f.latest_response_ms}ms</span> : null}
                    {f.latest_user_name && <span> · {f.latest_user_name}</span>}
                  </p>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="auth">
          <Card>
            <CardHeader><CardTitle className="text-base">Authentication health</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              <div className="grid sm:grid-cols-2 gap-3">
                <Card className="border-border">
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground uppercase">Apple Sign-In errors</p>
                    <p className="text-3xl font-bold mt-1">{appleSigninCount}</p>
                    <p className="text-[11px] text-muted-foreground mt-1">Auto-alert fires at 5+ in 5 min</p>
                  </CardContent>
                </Card>
                <Card className="border-border">
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground uppercase">Auth API 401/403 (last 7d)</p>
                    <p className="text-3xl font-bold mt-1">
                      {apiFailures.filter((f) => f.endpoint?.includes("/auth") && [401, 403].includes(f.status))
                        .reduce((s, f) => s + f.count, 0)}
                    </p>
                  </CardContent>
                </Card>
              </div>
              <p className="text-xs text-muted-foreground italic">Phase B will add: signin success rate, OAuth failure breakdown, native-bridge timeline.</p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="payments">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Payment endpoint failures (7d)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {paymentHealth.failures.length === 0 && <p className="text-sm text-muted-foreground">No payment-route failures captured.</p>}
              {paymentHealth.failures.map((f, idx) => (
                <div key={idx} className="p-3 bg-muted/20 rounded-lg flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-pink-400" />
                  <code className="text-xs flex-1 truncate">{f.endpoint}</code>
                  <Badge variant="outline" className="text-[10px]">x{f.count}</Badge>
                  <span className="text-[11px] text-muted-foreground ml-2">{timeAgo(f.latest)}</span>
                </div>
              ))}
              <p className="text-xs text-muted-foreground italic">Phase B will add: Stripe/RevenueCat webhook health, abandoned-checkout funnel, refund/chargeback feed.</p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="performance">
          <Card>
            <CardHeader><CardTitle className="text-base">Performance insights</CardTitle></CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Phase A captures slow API responses inside API Failures (filterable by `latest_response_ms`).
                Page-load and WebView startup metrics arrive in Phase B.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

function KpiCard({ icon, label, value }) {
  return (
    <Card className="border-border">
      <CardContent className="p-4 flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center">{icon}</div>
        <div>
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
          <p className="text-2xl font-bold">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

export default AdminReliabilityTab;
